// =============================================================================
// YDA E-Learning API Service
// =============================================================================
// This service handles all API communications with the YDA backend
// Automatically gets auth token from localStorage for each API call
// =============================================================================

import { storage } from '../utils/storage';

// API Configuration - Uses environment variable from deployment environment
const API_CONFIG = {
  BASE_URL: process.env.REACT_APP_API_BASE_URL,
  ENDPOINTS: {
    // GET API - Retrieves e-learning content for a specific module with user progress
    GET_MODULE_CONTENT: '/api/elearning/module', // GET {{base_url}}/elearning/module/{{module_id}}
    // POST API - Updates user's e-learning progress for a specific module
    UPDATE_PROGRESS: '/api/elearning/module',     // POST {{base_url}}/elearning/module/{{module_id}}/status
    // POST API - Exchange auth code for access token
    EXCHANGE_AUTH_CODE: '/api/elearning/exchange/code'
  }
};

// =============================================================================
// API Helper Functions
// =============================================================================

/**
 * Creates headers for API requests
 * Uses provided token or falls back to localStorage token
 * @param {string} customToken - Optional custom token to use instead of localStorage
 * @returns {Object} Headers object with authentication and content type
 * @throws {Error} If no valid token is found
 */
const createHeaders = (customToken = null) => {
  // Use provided token or fall back to localStorage
  const token = customToken || storage.getAuthToken();
  
  if (!token) {
    throw new Error('No valid authentication token found. Please login again.');
  }
  
  console.log('API: Using auth token', customToken ? '(from auth code exchange)' : '(from localStorage)');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

/**
 * Handles API responses and errors
 * @param {Response} response - Fetch response object
 * @returns {Promise} Parsed JSON response or throws error with code
 */
const handleApiResponse = async (response) => {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    
    // Create error object with code for CMS lookup
    const errorWithCode = new Error(errorData.message || errorData.code || `HTTP ${response.status}: ${response.statusText}`);
    errorWithCode.code = errorData.code; // Preserve backend error code
    
    // Handle specific error cases
    switch (response.status) {
      case 400:
        if (errorData.code === 'auth/invalid-code' || 
            errorData.message?.includes('Invalid or expired')) {
          errorWithCode.message = 'Authorization code is invalid or has expired.';
        }
        break;
      case 401:
        errorWithCode.message = 'No valid authentication token found. Please login again.';
        break;
      case 403:
        errorWithCode.message = 'Authorization code is invalid or has expired.';
        break;
      case 404:
        errorWithCode.message = 'Module not found. Please check the module ID in the URL.';
        break;
    }
    
    if (response.status >= 500) {
      errorWithCode.message = 'Server error occurred. Please try again later.';
    }
    
    throw errorWithCode;
  }
  return response.json();
};

// =============================================================================
// API Service Functions
// =============================================================================

export const apiService = {

  /**
   * POST API - Exchange auth code for access token
   * Called when auth_code is present in URL parameters
   * @param {string} authCode - The auth code from URL parameter
   * @returns {Promise<Object>} Token response with accessToken, refreshToken, expiresIn
   */
  exchangeAuthCode: async (authCode) => {
    try {
      console.log('API: Exchanging auth code for access token');
      
      // POST API call using environment variable: {{base_url}}/api/elearning/exchange/code
      const response = await fetch(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.EXCHANGE_AUTH_CODE}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ code: authCode })
        }
      );
      
      const data = await handleApiResponse(response);
      
      // Check if the API returned success: false (even with HTTP 200)
      if (!data.success) {
        const errorCode = data.code || 'unknown';
        const errorMessage = data.message || 'Auth code exchange failed';
        
        // Create error with code for CMS lookup
        const errorWithCode = new Error(errorMessage);
        errorWithCode.code = errorCode; // Preserve backend error code
        
        if (errorCode === 'auth/invalid-code' || errorMessage.includes('Invalid or expired')) {
          errorWithCode.message = 'Authorization code is invalid or has expired.';
        }
        
        throw errorWithCode;
      }
      
      return data;
      
    } catch (error) {
      console.error('API: Auth code exchange failed:', error);
      
      // Handle network errors
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error('Network connection error. Please check your internet connection and try again.');
      }
      
      throw error;
    }
  },

  /**
   * GET API - Retrieves e-learning content for a specific module with user progress
   * Called on app startup/refresh - matches Postman collection
   * @param {string} moduleId - The module ID from URL parameter
   * @param {string} customToken - Optional custom token to use instead of localStorage
   * @returns {Promise<Object>} Module content with user progress data
   */
  getModuleContent: async (moduleId, customToken = null) => {
    try {
      // GET API call matching Postman: GET {{base_url}}/elearning/module/{{module_id}}
      const response = await fetch(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.GET_MODULE_CONTENT}/${moduleId}`,
        {
          method: 'GET',
          headers: createHeaders(customToken) // Uses custom token or falls back to localStorage
        }
      );
      
      const data = await handleApiResponse(response);
      
      // Response structure from Postman:
      // {
      //   "success": true,
      //   "elearning": { "slides": [...] },
      //   "startSlide": 1,
      //   "language": "en"
      // }
      
      return data;
      
    } catch (error) {
      throw error;
    }
  },

  /**
   * POST API - Updates user's e-learning progress for a specific module
   * Called when user clicks Next button, changes language, completes audio, or exits
   * @param {Object} progressData - Progress data to save
   * @param {string} progressData.moduleId - The module ID from URL parameter
   * @param {number} progressData.slideNumber - Current slide number (1-based as per Postman)
   * @param {string} progressData.language - Current language code (en, ar, ur, ma)
   * @param {Object} progressData.quizState - Quiz state data (if on quiz slide)
   * @param {Object} progressData.allQuizStatuses - All previous quiz statuses
   * @param {boolean} progressData.isallSlidesCompleted - Whether all slides are completed
   * @param {Object} progressData.exitEvent - Exit event data (if user is exiting)
   * @param {string} customToken - Optional custom token to use instead of localStorage
   * @returns {Promise<void>} Promise that resolves when progress is saved
   */
  updateProgress: async (progressData, customToken = null) => {
    try {
      const requestBody = {
        slideNumber: progressData.slideNumber, // 1-based as per Postman
        language: progressData.language,         // "en", "ar", "ur", "ma"
        completedSlides: progressData.completedSlides || [], // Array of completed slide numbers
        isallSlidesCompleted: progressData.isallSlidesCompleted === true // Boolean: true when all slides are completed (explicitly check for true)
      };
      
      console.log('API Service: Request body isallSlidesCompleted:', {
        received: progressData.isallSlidesCompleted,
        type: typeof progressData.isallSlidesCompleted,
        final: requestBody.isallSlidesCompleted
      });

      // Add quiz state if provided (for quiz slides)
      if (progressData.quizState) {
        requestBody.quizState = progressData.quizState;
      }

      // Add all quiz statuses if provided (for comprehensive tracking)
      if (progressData.allQuizStatuses) {
        requestBody.allQuizStatuses = progressData.allQuizStatuses;
      }

      // Add exit event if provided (for exit tracking)
      if (progressData.exitEvent) {
        requestBody.exitEvent = progressData.exitEvent;
      }
      
      // POST API call: POST {{base_url}}/elearning/module/{{module_id}}/status
      const response = await fetch(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.UPDATE_PROGRESS}/${progressData.moduleId}/status`,
        {
          method: 'POST',
          headers: createHeaders(customToken), // Uses custom token or falls back to localStorage
          body: JSON.stringify(requestBody)
        }
      );
      
      const data = await handleApiResponse(response);
      
      // Response structure: { "success": true }
      return data;
      
    } catch (error) {
      throw error;
    }
  },

  /**
   * Validates API configuration
   * Checks if token exists in localStorage and base URL is configured
   * @returns {boolean} True if configuration is valid
   */
  validateConfig: () => {
    const hasToken = storage.hasValidToken();
    const hasBaseUrl = Boolean(API_CONFIG.BASE_URL);
    const isValid = hasBaseUrl && hasToken;
    
    // Log current API configuration
    console.log(`API Config: BaseURL=${API_CONFIG.BASE_URL}`);
    
    return isValid;
  }
};

export default apiService;